import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, ReplaySubject, Subject } from "rxjs";
import { catchError, concatMap, map, mapTo, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { UserSettings } from '../../models/user-settings.model';
import { User } from '../../models/user.model';
import { ColumnMapEntry, GraphService } from './graph.service';
import { GraphUserService } from './user.service';

interface SettingWriteRequest {
	key: string;
	value: string;
	result$: Subject<boolean>;
}

@Injectable({
	providedIn: 'root'
})
export class GraphUserSettingsService {

	static readonly columnMap: ReadonlyArray<ColumnMapEntry<UserSettings>> = [
		{ modelKey: "itemId", spFriendlyFieldName: "Id" },
		{ modelKey: "settings", spFriendlyFieldName: "Settings" },
		{ modelKey: "userId", spFriendlyFieldName: "UserID" }
	];

	private _userSettings = this.defaultUserSettings();
	get userSettings(): Observable<UserSettings> { return this._userSettings; }
	loaded: Subject<boolean> = new ReplaySubject<boolean>(1);


	private readonly sharePointListName = 'User_Settings';
	private mockIdCounter = 0;
	private readonly mockData: UserSettings = new UserSettings(this.getNextMockId(),
		{ dark: "false", lang: "en", hasSeenOrientation: "false", bookmarkIds: "" }, '6');
	private getNextMockId() { return this.mockIdCounter++; }

	private init$: Observable<boolean>;
	private readonly writeQueue$ = new Subject<SettingWriteRequest>();

	constructor(
		private readonly graphService: GraphService,
		private readonly userService: GraphUserService
	) {
		// writes are serialized: SharePoint rejects concurrent updates of the same item with a 409 Conflict
		this.writeQueue$.pipe(
			concatMap(request => this.writeSetting(request.key, request.value).pipe(
				catchError(() => of(false)),
				tap(result => {
					request.result$.next(result);
					request.result$.complete();
				})
			))
		).subscribe();
	}

	/** Loads the settings once; later calls replay the result of the first load instead of re-fetching. */
	init(): Observable<boolean> {
		if (!this.init$) {
			this.init$ = this.currentUser().pipe(
				switchMap(user => this.graphService
					.getListItems<UserSettings>(this.sharePointListName, UserSettings, GraphUserSettingsService.columnMap)
					.pipe(
						take(1),
						map(result => this.resolveUserSettings(result, user))
					)),
				tap(settings => {
					this._userSettings.next(settings);
					this.loaded.next(true);
				}),
				mapTo(true),
				shareReplay(1)
			);
		}
		return this.init$;
	}

	/** init() can be called while the profile request is still in flight, so wait for the user instead of reading a blank one. */
	private currentUser(): Observable<User> {
		const user = this.userService.getUser();
		return user ? of(user) : this.userService.getUserAsObservable().pipe(take(1));
	}

	public getUserSetting(key: string): string {
		return key ? this._userSettings.value.settings[key] : undefined;
	}

	public setUserSetting(key: string, value: string): Observable<boolean> {
		if (this.getUserSetting(key) === value) {
			return of(true);
		}

		const result$ = new ReplaySubject<boolean>(1);
		this.writeQueue$.next({ key, value, result$ });
		return result$.asObservable();
	}

	/** Applies the change to the latest known settings, so queued writes don't overwrite each other. */
	private writeSetting(key: string, value: string): Observable<boolean> {
		const current = this._userSettings.value;
		const newSettings = { ...current.settings, [key]: value };
		const newUserSettings = { ...current, settings: newSettings };

		return this.upsertUserSettings(newUserSettings).pipe(
			take(1),
			tap((result: UserSettings) => this._userSettings.next(result)), // re-publish to tell everyone about the "new" values
			mapTo(true)
		);
	}

	private resolveUserSettings(storedSettings: UserSettings[], user: User): UserSettings {
		const loginName = user ? user.loginName : undefined;
		const found = loginName ? storedSettings.find(s => s.userId === loginName) : undefined;
		if (found) {
			found.settings = JSON.parse(found.settings);
			return found;
		}

		const defaultSettings = this.defaultUserSettings().value;
		defaultSettings.userId = loginName;
		return defaultSettings;
	}

	/** Sharepoint WRITE */
	private upsertUserSettings(userSettings: UserSettings): Observable<UserSettings> {
		if (userSettings.userId == null) {
			// don't save anonymous user settings
			return of(userSettings);
		}

		const item = { Settings: JSON.stringify(userSettings.settings), UserID: userSettings.userId };
		if (userSettings.itemId === null) {
			return this.graphService.addListItem(this.sharePointListName, item).pipe(map(a => {
				userSettings.itemId = a;
				return userSettings;
			}));
		}
		else {
			return this.graphService.updateListItem(this.sharePointListName, userSettings.itemId, item).pipe(map(a => {
				return userSettings;
			}));
		}
	}
	private defaultUserSettings(): BehaviorSubject<UserSettings> {
		return new BehaviorSubject<UserSettings>(new UserSettings(null, { dark: "false", lang: "en", hasSeenOrientation: "false", bookmarkIds: "",  captions:"false"}, null));
	}
}
